import React from 'react';
import { cn } from '../../lib/utils';
import { Lightbulb } from 'lucide-react';
import { Button } from './Button';

interface EmptyStateAction {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'ghost';
}

interface EmptyStateProps {
  icon: React.ElementType;
  title: string;
  description: string;
  actions?: EmptyStateAction[];
  tips?: string[];
  className?: string;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon: Icon,
  title,
  description,
  actions = [],
  tips = [],
  className,
}) => {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center py-16 px-8 text-center',
        'bg-white border-8 border-dashed border-black dark:border-white dark:bg-black',
        className
      )}
    >
      {/* Icon */}
      <div className="w-20 h-20 bg-[#F5F5F0] border-4 border-black dark:border-white flex items-center justify-center mb-8 dark:bg-slate-900">
        <Icon size={36} className="text-black dark:text-white" />
      </div>

      {/* Title */}
      <h3 className="text-2xl md:text-3xl font-black font-display uppercase tracking-tighter text-black dark:text-white mb-3">
        {title}
      </h3>

      {/* Description */}
      <p className="text-sm font-mono uppercase tracking-widest text-gray-500 dark:text-gray-400 max-w-md mb-8 leading-relaxed">
        {description}
      </p>

      {/* Actions */}
      {actions.length > 0 && (
        <div className="flex flex-wrap items-center justify-center gap-4 mb-8">
          {actions.map((action, idx) => (
            <Button
              key={idx}
              variant={action.variant || (idx === 0 ? 'primary' : 'secondary')}
              onClick={action.onClick}
              className={cn(
                'uppercase font-bold tracking-widest text-xs py-4 px-8 rounded-none border-2 border-black dark:border-white',
                idx === 0 && 'bg-[#0044FF] text-white hover:bg-black dark:hover:bg-white dark:hover:text-black shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]',
                idx > 0 && 'bg-white dark:bg-black text-black dark:text-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]'
              )}
            >
              {action.label}
            </Button>
          ))}
        </div>
      )}

      {/* Tips */}
      {tips.length > 0 && (
        <div className="w-full max-w-lg bg-[#FFD600] border-4 border-black p-5 text-left">
          <div className="flex items-center gap-2 mb-3">
            <Lightbulb size={16} className="text-black" />
            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-black font-mono">
              Quick Tips
            </span>
          </div>
          <ul className="space-y-2">
            {tips.map((tip, idx) => (
              <li
                key={idx}
                className="text-xs font-bold text-black leading-relaxed flex gap-2"
              >
                <span className="text-black font-black">•</span>
                {tip}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
